import { useState } from "react";
import { Text } from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";
import { cn } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import type { EvaluationPanelMember } from "../../../types/panel.type";
import { useCurrentUserId } from "hooks/auth";

interface PanelMemberTableProps {
	members: EvaluationPanelMember[];
	onRemove?: (memberId: number) => void;
	selectedResendIds?: number[];
	onToggleResend?: (memberId: number) => void;
	hasInvitedMembers?: boolean;
}

const getTypeBadgeClasses = (memberType: EvaluationPanelMember["member_type"]): string => {
	switch (memberType) {
		case "internal":
			return "bg-blue-100 text-blue-700";
		case "external":
			return "bg-amber-100 text-amber-700";
		case "ai":
			return "bg-purple-100 text-purple-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

const getStatusBadgeClasses = (status: EvaluationPanelMember["status"]): string => {
	switch (status) {
		case "accepted":
			return "bg-green-100 text-green-700";
		case "invited":
			return "bg-blue-100 text-blue-700";
		case "pending":
			return "bg-gray-100 text-gray-600";
		case "declined":
			return "bg-red-100 text-red-700";
		case "revoked":
			return "bg-gray-200 text-gray-500";
		default:
			return "bg-gray-100 text-gray-600";
	}
};

export const PanelMemberTable: React.FC<PanelMemberTableProps> = ({
	members,
	onRemove,
	selectedResendIds = [],
	onToggleResend,
	hasInvitedMembers = false,
}) => {
	const [memberToRemove, setMemberToRemove] = useState<EvaluationPanelMember | null>(null);
	const currentUserId = useCurrentUserId();

	const handleConfirmRemove = () => {
		if (memberToRemove && onRemove) {
			onRemove(memberToRemove.id);
			setMemberToRemove(null);
		}
	};

	if (members.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="panel-members-empty"
			>
				No panel members added yet. Use the inputs below to add members.
			</div>
		);
	}

	return (
		<>
			<div className="w-full overflow-x-auto" data-testid="panel-members-table">
				<table className="w-full min-w-[600px] border-collapse text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
							{hasInvitedMembers && <th className="w-10 px-3 py-3" />}
							<th className="px-3 py-3">Name</th>
							<th className="px-3 py-3">Email</th>
							<th className="px-3 py-3">Type</th>
							<th className="px-3 py-3">Status</th>
							{onRemove && <th className="px-3 py-3 text-right">Actions</th>}
						</tr>
					</thead>
					<tbody>
						{members.map((member) => {
							const isInvited = member.status === "invited";
							const isChecked = selectedResendIds.includes(member.id);
							const isCurrent = currentUserId === member.user_id;
							return (
								<tr
									key={member.id}
									className="border-b border-gray-100 transition-colors hover:bg-gray-50"
								>
									{hasInvitedMembers && (
										<td className="px-3 py-3">
											{isInvited && onToggleResend && (
												<input
													type="checkbox"
													checked={isChecked}
													onChange={() => onToggleResend(member.id)}
													className="h-4 w-4 rounded border-gray-300 cursor-pointer"
													data-testid={`resend-checkbox-${member.id}`}
												/>
											)}
										</td>
									)}
									<td className="px-3 py-3 font-medium text-gray-900">
										{member.name || member.user?.full_name || "-"}{isCurrent && " (you)"}
									</td>
									<td className="px-3 py-3 text-gray-600">
										{member.email}
									</td>
									<td className="px-3 py-3">
										<span
											className={cn(
												"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
												getTypeBadgeClasses(member.member_type)
											)}
										>
											{member.member_type}
										</span>
									</td>
									<td className="px-3 py-3">
										<span
											className={cn(
												"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
												getStatusBadgeClasses(member.status)
											)}
										>
											{member.status}
										</span>
									</td>
									{onRemove && (
										<td className="px-3 py-3">
											<div className="flex items-center justify-end">
												<button
													type="button"
													onClick={() => setMemberToRemove(member)}
													className="rounded-md border border-gray-300 p-1.5 text-gray-500 transition-colors hover:border-red-400 hover:text-red-500"
													aria-label={`Remove ${member.name || member.email}`}
													data-testid={`remove-member-${member.id}`}
												>
													<FiTrash2 size={14} />
												</button>
											</div>
										</td>
									)}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<Modal
				isOpen={memberToRemove !== null}
				onClose={() => setMemberToRemove(null)}
				title="Remove Panel Member"
				hideCloseButton
				iconType="error"
				size="sm"
				closeOnEsc
				closeOnOverlayClick
				primaryButton={{
					text: "Remove",
					onClick: handleConfirmRemove,
					icon: <FiTrash2 size={16} />,
				}}
				secondaryButton={{
					text: "Cancel",
					onClick: () => setMemberToRemove(null),
				}}
			>
				<Text textAlign="center" color="gray.600">
					Are you sure you want to remove{" "}
					<strong>{memberToRemove?.name || memberToRemove?.email}</strong> from
					the evaluation panel?
				</Text>
			</Modal>
		</>
	);
};
